import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import apiClient from "../../../services/apiClient";
import { ASSIGNMENT_PLAN_REQUIRED_MESSAGE, canCreateAssignment } from "../../../utils/planUtils";
import MathBankAssignModal from "./MathBankAssignModal";
import {
  DB_OPTION_LIST,
  KIND_LABEL,
  MAX_TOTAL,
  SOURCE_OPTION_LIST,
  imageListOf,
  plainMathText,
} from "./mathBankShared";
import "../../../styles/WorksheetModalV2.css";
import "./MathBankAssignModal.css";
import "./MathBankPage.css";

// 수학 계정의 '문제뱅크' 탭(2026-11-03 대표: 영어 문제뱅크처럼 창 없이 안에서 바로 낸다).
// 1. 범위: [단원] 분류표 또는 [내 DB 폴더](메뉴 → 폴더 → 문서). 문항 수는 어디에도 보이지 않는다(대표).
// 2. 조건으로 뽑거나, 문서를 열어 문항을 직접 체크해 담는다. 담은 문제는 바꾸기(쌍둥이 · 닮은 문제) · 빼기.
// 3. '출제하기' 를 누르면 제목 · 복습 · 마감일 · 학생만 고르는 창(MathBankAssignModal)이 뜨고, 끝나면 닫힌다.
const RANGE_TAB_LIST = [
  { value: "unit", label: "단원" },
  { value: "doc", label: "내 DB 폴더" },
];

const MathBankPage = () => {
  const { user } = useSelector((state) => state.auth);
  const canAssign = canCreateAssignment(user.plan);

  const [rangeTab, setRangeTab] = useState("unit");
  const [unitData, setUnitData] = useState(null);
  const [subjectCode, setSubjectCode] = useState("");
  const [openCodeList, setOpenCodeList] = useState([]);
  const [unitCodeList, setUnitCodeList] = useState([]);
  const [skillCodeList, setSkillCodeList] = useState([]);
  const [typeCodeList, setTypeCodeList] = useState([]);

  const [docData, setDocData] = useState(null);
  const [docSectionKey, setDocSectionKey] = useState("");
  const [openFolderKeyList, setOpenFolderKeyList] = useState([]);
  const [docIdList, setDocIdList] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docProblemPage, setDocProblemPage] = useState({ page: 1, pages: 1, problem_list: [] });
  const [checkedProblemIdList, setCheckedProblemIdList] = useState([]);

  const [difficultyList, setDifficultyList] = useState([]);
  const [kindList, setKindList] = useState([]);
  const [dbList, setDbList] = useState(["mine", "public"]);
  const [source, setSource] = useState("both");
  const [total, setTotal] = useState(20);

  const [problemList, setProblemList] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [swappingId, setSwappingId] = useState("");
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [defaultTitle, setDefaultTitle] = useState("");

  useEffect(() => {
    apiClient
      .get("/api/v1/assignment/math/bank/units", { withAuth: true })
      .then((response) => {
        setUnitData(response.data);
        setSubjectCode(response.data.subjects?.[0]?.code || "");
      })
      .catch((error) => setUnitData({ subjects: [], error: error?.response?.data?.error || "분류표를 불러오지 못했습니다." }));
  }, []);

  // 내 DB 폴더는 그 탭을 처음 열 때 받는다
  useEffect(() => {
    if (rangeTab !== "doc" || docData) return;
    apiClient
      .get("/api/v1/assignment/math/bank/docs", { withAuth: true })
      .then((response) => {
        setDocData(response.data);
        setDocSectionKey(response.data.sections?.[0]?.section || "");
      })
      .catch((error) => setDocData({ sections: [], error: error?.response?.data?.error || "내 DB 폴더를 불러오지 못했습니다." }));
  }, [rangeTab, docData]);

  const subjectList = useMemo(() => unitData?.subjects || [], [unitData]);
  const subject = subjectList.find((one) => one.code === subjectCode);
  const levelList = useMemo(() => [...new Set(subjectList.map((one) => one.level))], [subjectList]);
  const docSectionList = useMemo(() => docData?.sections || [], [docData]);
  const docSection = docSectionList.find((one) => one.section === docSectionKey);
  const docTitleById = useMemo(() => {
    const titleById = {};
    docSectionList.forEach((section) =>
      section.folders.forEach((folder) => folder.docs.forEach((doc) => (titleById[doc.id] = doc.title)))
    );
    return titleById;
  }, [docSectionList]);

  const toggleIn = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((one) => one !== value) : [...list, value]);

  // 단원을 고르면 그 아래 고른 것은 지운다(겹쳐 보내지 않는다). 아래를 고르면 그 단원은 푼다
  const toggleUnit = (unit) => {
    const childSkillList = (unit.skills || []).map((skill) => skill.code);
    const childTypeList = (unit.skills || []).flatMap((skill) => (skill.types || []).map((type) => type.code));
    setSkillCodeList((list) => list.filter((code) => !childSkillList.includes(code)));
    setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    toggleIn(unitCodeList, setUnitCodeList, unit.code);
  };
  const toggleSkill = (unit, skill) => {
    const childTypeList = (skill.types || []).map((type) => type.code);
    setUnitCodeList((list) => list.filter((code) => code !== unit.code));
    setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    toggleIn(skillCodeList, setSkillCodeList, skill.code);
  };
  const toggleType = (unit, skill, type) => {
    setUnitCodeList((list) => list.filter((code) => code !== unit.code));
    setSkillCodeList((list) => list.filter((code) => code !== skill.code));
    toggleIn(typeCodeList, setTypeCodeList, type.code);
  };

  const isDocMode = rangeTab === "doc";
  const hasRange = isDocMode ? docIdList.length > 0 : unitCodeList.length + skillCodeList.length + typeCodeList.length > 0;

  const firstRangeTitle = () => {
    if (isDocMode) return docTitleById[docIdList[0]] || "";
    const firstUnit = (subject?.units || []).find(
      (unit) =>
        unitCodeList.includes(unit.code) ||
        (unit.skills || []).some(
          (skill) => skillCodeList.includes(skill.code) || (skill.types || []).some((type) => typeCodeList.includes(type.code))
        )
    );
    return [subject?.name, firstUnit?.name].filter(Boolean).join(" ");
  };
  const pick = async () => {
    if (!hasRange) return alert(isDocMode ? "문서를 골라주세요." : "단원을 골라주세요.");
    if (!isDocMode && !dbList.length) return alert("내 문제나 문제은행을 골라주세요.");
    if (isBusy) return;
    setIsBusy(true);
    try {
      const condition = isDocMode
        ? { docs: docIdList, total, difficulties: difficultyList, kinds: kindList, dbs: ["mine"], source }
        : {
            units: unitCodeList,
            skills: skillCodeList,
            types: typeCodeList,
            total,
            difficulties: difficultyList,
            kinds: kindList,
            dbs: dbList,
            source,
          };
      const response = await apiClient.post("/api/v1/assignment/math/bank/pick", condition, { withAuth: true });
      if (!response.data.problem_list.length) return alert("조건에 맞는 문제가 없습니다.");
      setProblemList(response.data.problem_list);
      setDefaultTitle(firstRangeTitle());
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 뽑지 못했습니다.");
    } finally {
      setIsBusy(false);
    }
  };

  const docViewerRef = useRef(null);
  const openDoc = async (doc, page = 1) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const response = await apiClient.get(
        `/api/v1/assignment/math/bank/doc-problems?doc_id=${doc.id}&page=${page}&source=${source}`,
        { withAuth: true }
      );
      setViewingDoc(doc);
      setDocProblemPage(response.data);
      if (page === 1) setCheckedProblemIdList([]);
      // 문서 목록이 길어 문항 보기가 화면 아래에 열린다. 열린 자리로 데려간다
      requestAnimationFrame(() => docViewerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    } catch (error) {
      alert(error?.response?.data?.error || "문서 문항을 불러오지 못했습니다.");
    } finally {
      setIsBusy(false);
    }
  };

  // 문서에서 체크한 문제를 담은 문제 뒤에 붙인다(이미 담긴 것은 건너뛴다)
  const addCheckedProblems = () => {
    const pickedList = docProblemPage.problem_list.filter((one) => checkedProblemIdList.includes(one.problem_id));
    if (!pickedList.length) return alert("담을 문제를 체크해주세요.");
    const existingIdSet = new Set(problemList.map((one) => one.problem_id));
    const nextList = [...problemList, ...pickedList.filter((one) => !existingIdSet.has(one.problem_id))];
    if (nextList.length > MAX_TOTAL) return alert(`한 번에 ${MAX_TOTAL}문제까지 낼 수 있어요.`);
    setProblemList(nextList);
    setCheckedProblemIdList([]);
    if (!defaultTitle) setDefaultTitle(viewingDoc?.title || "");
  };

  const swap = async (problemId) => {
    if (swappingId) return;
    setSwappingId(problemId);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/swap",
        { problem_id: problemId, screen_problem_ids: problemList.map((one) => one.problem_id), dbs: dbList },
        { withAuth: true }
      );
      if (!response.data.problem) return alert("닮은 문제가 더 없습니다.");
      setProblemList((list) => list.map((one) => (one.problem_id === problemId ? response.data.problem : one)));
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 바꾸지 못했습니다.");
    } finally {
      setSwappingId("");
    }
  };

  const openAssign = () => {
    if (!problemList.length) return alert("문제를 담아주세요.");
    if (!canAssign) return alert(ASSIGNMENT_PLAN_REQUIRED_MESSAGE);
    setIsAssignOpen(true);
  };

  const renderChipList = (optionList, isActive, onPick) => (
    <div className="math-bank-modal__chip-list">
      {optionList.map((one) => (
        <button
          key={one.value}
          type="button"
          className={`ws-filter-chip${isActive(one.value) ? " active" : ""}`}
          onClick={() => onPick(one.value)}
        >
          {one.label}
        </button>
      ))}
    </div>
  );

  const renderProblemCard = (problem, index, actions) => (
    <li key={problem.problem_id} className="math-bank-modal__problem">
      <div className="math-bank-modal__problem-meta">
        {actions.checkbox}
        <span className="math-bank-modal__number">{index + 1}</span>
        {problem.difficulty_label && <span className="math-bank-modal__badge">{problem.difficulty_label}</span>}
        {KIND_LABEL[problem.answer_type] && <span className="math-bank-modal__badge">{KIND_LABEL[problem.answer_type]}</span>}
        {problem.origin && (
          <span className="math-bank-modal__badge">{problem.origin === "original" ? "원본" : "변형"}</span>
        )}
        {(problem.skill_name || problem.type_label) && (
          <span className="math-bank-modal__tag">{problem.type_name || problem.skill_name || problem.type_label}</span>
        )}
        {actions.buttons && <span className="math-bank-modal__problem-actions">{actions.buttons}</span>}
      </div>
      <div className="math-bank-modal__question">{plainMathText(problem.question)}</div>
      {imageListOf(problem).map((url) => (
        <img key={url} className="math-bank-modal__image" src={url} alt="" />
      ))}
      {(problem.choice_list || []).length > 0 && (
        <div className="math-bank-modal__choice-list">
          {problem.choice_list.map((choice, choiceIndex) => (
            <span key={choiceIndex}>
              {choiceIndex + 1}. {plainMathText(choice)}
            </span>
          ))}
        </div>
      )}
    </li>
  );

  const renderUnitRange = () => (
    <>
      {!unitData && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
      {unitData?.error && <p className="math-bank-page__empty">{unitData.error}</p>}
      {levelList.map((level) => (
        <div key={level} className="math-bank-modal__field">
          <div className="math-bank-modal__label">{level}</div>
          {renderChipList(
            subjectList.filter((one) => one.level === level).map((one) => ({ value: one.code, label: one.name })),
            (value) => value === subjectCode,
            setSubjectCode
          )}
        </div>
      ))}
      <div className="math-bank-modal__tree">
        {(subject?.units || []).map((unit) => (
          <div key={unit.code} className="math-bank-modal__unit">
            <div className="math-bank-modal__row">
              <label className="math-bank-modal__check">
                <input type="checkbox" checked={unitCodeList.includes(unit.code)} onChange={() => toggleUnit(unit)} />
                <span>{unit.name}</span>
              </label>
              {(unit.skills || []).length > 0 && (
                <button type="button" className="math-bank-modal__open" onClick={() => toggleIn(openCodeList, setOpenCodeList, unit.code)}>
                  {openCodeList.includes(unit.code) ? "접기" : "소분류"}
                </button>
              )}
            </div>
            {openCodeList.includes(unit.code) &&
              (unit.skills || []).map((skill) => (
                <div key={skill.code} className="math-bank-modal__skill">
                  <div className="math-bank-modal__row">
                    <label className="math-bank-modal__check">
                      <input type="checkbox" checked={skillCodeList.includes(skill.code)} onChange={() => toggleSkill(unit, skill)} />
                      <span>{skill.name}</span>
                    </label>
                    {(skill.types || []).length > 0 && (
                      <button type="button" className="math-bank-modal__open" onClick={() => toggleIn(openCodeList, setOpenCodeList, skill.code)}>
                        {openCodeList.includes(skill.code) ? "접기" : "세부유형"}
                      </button>
                    )}
                  </div>
                  {openCodeList.includes(skill.code) &&
                    (skill.types || []).map((type) => (
                      <div key={type.code} className="math-bank-modal__row math-bank-modal__type">
                        <label className="math-bank-modal__check">
                          <input type="checkbox" checked={typeCodeList.includes(type.code)} onChange={() => toggleType(unit, skill, type)} />
                          <span>{type.name}</span>
                        </label>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
      </div>
    </>
  );

  const renderDocRange = () => (
    <>
      {!docData && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
      {docData?.error && <p className="math-bank-page__empty">{docData.error}</p>}
      {docData && !docData.error && !docSectionList.length && <p className="math-bank-page__empty">내 DB 에 문서가 없어요.</p>}
      {docSectionList.length > 0 &&
        renderChipList(
          docSectionList.map((one) => ({ value: one.section, label: one.name })),
          (value) => value === docSectionKey,
          setDocSectionKey
        )}
      <div className="math-bank-modal__tree math-bank-page__doc-tree">
        {(docSection?.folders || []).map((folder) => {
          const folderKey = `${docSectionKey}:${folder.id ?? "none"}`;
          const isOpen = openFolderKeyList.includes(folderKey) || docSection.folders.length === 1;
          return (
            <div key={folderKey} className="math-bank-modal__unit">
              <div className="math-bank-modal__row">
                <button type="button" className="math-bank-page__folder" onClick={() => toggleIn(openFolderKeyList, setOpenFolderKeyList, folderKey)}>
                  {isOpen ? "▾" : "▸"} {folder.name}
                </button>
              </div>
              {isOpen &&
                folder.docs.map((doc) => (
                  <div key={doc.id} className="math-bank-modal__row math-bank-modal__type">
                    <label className="math-bank-modal__check">
                      <input type="checkbox" checked={docIdList.includes(doc.id)} onChange={() => toggleIn(docIdList, setDocIdList, doc.id)} />
                      <span>{doc.title}</span>
                    </label>
                    <button type="button" className="math-bank-modal__open" onClick={() => openDoc(doc)}>
                      문항 보기
                    </button>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </>
  );

  const renderDocProblems = () => {
    if (!viewingDoc) return null;
    const pageProblemIdList = docProblemPage.problem_list.map((one) => one.problem_id);
    const isAllChecked = pageProblemIdList.length > 0 && pageProblemIdList.every((id) => checkedProblemIdList.includes(id));
    return (
      <section ref={docViewerRef} className="math-bank-page__card">
        <div className="math-bank-page__card-head">
          <h4>{viewingDoc.title}</h4>
          <div className="math-bank-page__head-actions">
            <button
              type="button"
              className="math-bank-modal__action"
              onClick={() => setCheckedProblemIdList(isAllChecked ? [] : pageProblemIdList)}
            >
              {isAllChecked ? "모두 풀기" : "모두 체크"}
            </button>
            <button type="button" className="math-bank-modal__open-button" onClick={addCheckedProblems}>
              체크한 문제 담기
            </button>
            <button type="button" className="math-bank-modal__action" onClick={() => setViewingDoc(null)}>
              닫기
            </button>
          </div>
        </div>
        <ol className="math-bank-modal__problem-list">
          {docProblemPage.problem_list.map((problem, index) =>
            renderProblemCard(problem, index, {
              checkbox: (
                <input
                  type="checkbox"
                  checked={checkedProblemIdList.includes(problem.problem_id)}
                  onChange={() => toggleIn(checkedProblemIdList, setCheckedProblemIdList, problem.problem_id)}
                />
              ),
            })
          )}
        </ol>
        {docProblemPage.pages > 1 && (
          <div className="math-bank-page__pager">
            {Array.from({ length: docProblemPage.pages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={`ws-filter-chip${page === docProblemPage.page ? " active" : ""}`}
                onClick={() => openDoc(viewingDoc, page)}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="math-bank-page">
      <h3 className="section-title">문제은행</h3>

      <div className="math-bank-page__grid">
        <section className="math-bank-page__card">
          <div className="math-bank-page__card-head">
            <h4>범위</h4>
            {renderChipList(RANGE_TAB_LIST, (value) => value === rangeTab, setRangeTab)}
          </div>
          {rangeTab === "unit" ? renderUnitRange() : renderDocRange()}
        </section>

        <section className="math-bank-page__card">
          <div className="math-bank-page__card-head">
            <h4>조건</h4>
          </div>
          {!isDocMode && (
            <div className="math-bank-modal__field">
              <div className="math-bank-modal__label">문제</div>
              {renderChipList(DB_OPTION_LIST, (value) => dbList.includes(value), (value) => toggleIn(dbList, setDbList, value))}
            </div>
          )}
          {(isDocMode || dbList.includes("mine")) && (
            <div className="math-bank-modal__field">
              <div className="math-bank-modal__label">원본 · 변형</div>
              {renderChipList(SOURCE_OPTION_LIST, (value) => value === source, setSource)}
            </div>
          )}
          <div className="math-bank-modal__field">
            <div className="math-bank-modal__label">난이도</div>
            {renderChipList(
              (unitData?.difficulties || []).map((one) => ({ value: one, label: one })),
              (value) => difficultyList.includes(value),
              (value) => toggleIn(difficultyList, setDifficultyList, value)
            )}
          </div>
          <div className="math-bank-modal__field">
            <div className="math-bank-modal__label">형식</div>
            {renderChipList(
              (unitData?.kinds || []).map((one) => ({ value: one.code, label: one.name })),
              (value) => kindList.includes(value),
              (value) => toggleIn(kindList, setKindList, value)
            )}
          </div>
          <div className="math-bank-modal__field">
            <div className="math-bank-modal__label">문항 수</div>
            <input
              type="number"
              className="math-bank-modal__total"
              min={1}
              max={MAX_TOTAL}
              value={total}
              onChange={(event) => setTotal(Math.max(1, Math.min(MAX_TOTAL, Number(event.target.value) || 1)))}
            />
          </div>
          <button type="button" className="math-bank-page__pick" onClick={pick}>
            {isBusy ? "뽑는 중" : problemList.length ? "다시 뽑기" : "뽑기"}
          </button>
        </section>
      </div>

      {renderDocProblems()}

      {problemList.length > 0 && (
        <section className="math-bank-page__card">
          <div className="math-bank-page__card-head">
            <h4>담은 문제 {problemList.length}</h4>
            <div className="math-bank-page__head-actions">
              <button type="button" className="math-bank-modal__action" onClick={() => setProblemList([])}>
                비우기
              </button>
              <button type="button" className="math-bank-modal__open-button" onClick={openAssign}>
                출제하기
              </button>
            </div>
          </div>
          <ol className="math-bank-modal__problem-list">
            {problemList.map((problem, index) =>
              renderProblemCard(problem, index, {
                buttons: (
                  <>
                    <button type="button" className="math-bank-modal__action" onClick={() => swap(problem.problem_id)}>
                      {swappingId === problem.problem_id ? "바꾸는 중" : "바꾸기"}
                    </button>
                    <button
                      type="button"
                      className="math-bank-modal__action"
                      onClick={() => setProblemList((list) => list.filter((one) => one.problem_id !== problem.problem_id))}
                    >
                      빼기
                    </button>
                  </>
                ),
              })
            )}
          </ol>
        </section>
      )}

      {isAssignOpen && (
        <MathBankAssignModal
          problemList={problemList}
          defaultTitle={defaultTitle}
          onClose={() => setIsAssignOpen(false)}
          onAssigned={() => {
            setIsAssignOpen(false);
            setProblemList([]);
            setDefaultTitle("");
          }}
        />
      )}
    </div>
  );
};

export default MathBankPage;
